import { Button, Stack } from '@mui/material'
import isEmpty from 'lodash/isEmpty'
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { InformationModal } from 'utoniq-ui'

import {
  ApplyPreSaleLotteryInput,
  Fee,
  Maybe,
  PaymentDetailFragment,
  PaymentMethod,
  PreSaleLotteryDetailFragment,
  PreSaleStatus,
} from '@/clients/utoniq-core/schema'
import { useForm, UseFormProps, useRouter, useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { setPreference } from '@/core/native/storage'
import { usePrivateAction } from '@/lib/privateAction'

import {
  ButtonCell,
  CautionTextCell,
  CommissionListCell,
  PaymentInfoCell,
  TicketListCell,
} from './components'

type LotteryDetailFormProps = {
  preSale: PreSaleLotteryDetailFragment
  paymentSource?: Maybe<PaymentDetailFragment>
  originFullNameValue?: string
  onSubmit: (values: ApplyPreSaleLotteryValue | undefined) => Promise<void>
  onReadyPaymentMethod: (
    values: ApplyPreSaleLotteryValue | undefined
  ) => Promise<void>
  paymentFees?: Maybe<Fee[]>
  selectMultipleItem?: boolean
  choices:
    | { itemId: string; skuId: string | undefined; count: number }[]
    | undefined
  isAppliedUser: boolean
}

export type ApplyPreSaleLotteryValue = Omit<
  ApplyPreSaleLotteryInput,
  'choices' | 'purchaseCountChoices'
> & {
  choices: { itemId: string; skuId: string | undefined }[]
  purchaseCountChoices: { count: number }[]
  preSaleId: string
}

const schema: UseFormProps['schema'] = o => ({
  preSaleId: o.string(),
  paymentMethod: o.string(),
  fullName: o.string().when('paymentMethod', {
    is: PaymentMethod.Konbini,
    then: o.string().label('氏名'),
    otherwise: o.string().optional().allow(null),
  }),
  choices: o.array().items(
    o.object({
      itemId: o.string(),
      skuId: o.string().optional().allow(null),
    })
  ),
  purchaseCountChoices: o.array().items(
    o.object({
      count: o.number(),
    })
  ),
})

export const LotteryDetailForm: Organism<LotteryDetailFormProps> = ({
  preSale,
  paymentSource,
  originFullNameValue,
  paymentFees,
  onSubmit,
  onReadyPaymentMethod,
  selectMultipleItem = true,
  choices,
  isAppliedUser,
}) => {
  const { t } = useTranslation('lotteryDetail')
  const { handleLoginModal } = usePrivateAction()

  const { router, generatePath, routerPages } = useRouter()
  const {
    watch,
    setValue,
    handleSubmit,
    register,
    control,
    getValues,
    formState: { isSubmitting, errors },
  } = useForm<ApplyPreSaleLotteryValue>({
    schema,
    defaultValues: {
      ...defaultValues,
      preSaleId: preSale.id,
      // 予め選択された状態の場合（ストアから直接遷移）
      choices: choices
        ? choices.map(({ itemId, skuId }) => ({
            itemId: itemId,
            skuId: skuId,
          }))
        : defaultValues.choices,
      purchaseCountChoices: choices
        ? choices.map(({ count }) => ({ count }))
        : defaultValues.purchaseCountChoices,
      paymentMethod: preSale.paymentMethods?.[0] ?? defaultValues.paymentMethod,
    },
  })
  // メンバーシップ限定モーダルの表示状態
  const [membershipModalOpen, toggleMembershipModalOpen] = useReducer(
    (state: boolean) => !state,
    false
  )
  // 未入力の「第n希望」要素がフィールドに含まれているかどうか
  const [hasEmptyField, setHasEmptyField] = useState(!choices?.length)
  // 入力しているフィールドがあるかどうか
  const hasValidField = useMemo(() => {
    return (
      !!watch('choices').filter(
        (choice: { itemId: string; skuId: string | undefined }) => choice.itemId
      ).length &&
      !!watch('purchaseCountChoices').filter(
        (_, index) => !!watch('choices')[index]?.itemId
      ).length
    )
  }, [watch('choices'), watch('purchaseCountChoices')])

  const paymentMethodWatch = watch('paymentMethod')
  const isStopSalling =
    preSale.lottery?.applicationStartedAt > DateTime.nowUnixMilliseconds() ||
    preSale.lottery?.applicationEndedAt < DateTime.nowUnixMilliseconds()
  const isBeforeReceiption =
    preSale.preSaleStatus === PreSaleStatus.BeforeReceiption

  const filterEmptyItem = useCallback(() => {
    // 配列の中の空要素を除外する処理
    setValue(
      'purchaseCountChoices',
      watch('purchaseCountChoices').filter(
        (_, index) => !!watch('choices')[index]?.itemId
      )
    )
    setValue(
      'choices',
      watch('choices').filter(
        (choice: { itemId: string; skuId: string | undefined }) => choice.itemId
      )
    )
    setHasEmptyField(false)
  }, [setValue, watch])

  // 離脱時に入力値を保存
  useEffect(() => {
    return () => {
      ;(async () => {
        await setPreference('preSaleLotteryApplicationInput', watch())
      })()
    }
  }, [])

  return (
    <OrganismFrame>
      {(!isStopSalling || isBeforeReceiption) && !isAppliedUser && (
        <>
          <TicketListCell
            items={preSale.items}
            control={control}
            errors={errors}
            maxChoice={selectMultipleItem ? preSale.lottery?.maxChoice : 1}
            maxSelectItem={preSale.lottery?.purchasableCount}
            hasEmptyField={hasEmptyField}
            hasValidField={hasValidField}
            setHasEmptyField={setHasEmptyField}
            isBeforeReceiption={isBeforeReceiption}
            toggleMembershipModalOpen={toggleMembershipModalOpen}
            isAccessible={preSale.isAccessible}
          />

          {!isBeforeReceiption && (
            <PaymentInfoCell
              paymentMethods={
                preSale.paymentMethods ?? [
                  PaymentMethod.CreditCard,
                  PaymentMethod.Konbini,
                ]
              }
              isAccessible={preSale.isAccessible}
              toggleMembershipModalOpen={toggleMembershipModalOpen}
              paymentFees={paymentFees}
              value={paymentMethodWatch}
              setValue={setValue}
              paymentSource={paymentSource}
              originFullNameValue={originFullNameValue ?? ''}
              inputFullNameProps={{
                ...register('fullName'),
                error: errors.fullName,
              }}
              error={errors.paymentMethod?.message}
              onRegister={() => {
                if (hasValidField) {
                  filterEmptyItem()
                }

                setTimeout(() => {
                  onReadyPaymentMethod(getValues())
                }, 300)
              }}
            />
          )}
        </>
      )}
      <CommissionListCell
        paymentFees={paymentFees}
        isKonbini={watch('paymentMethod') === PaymentMethod.Konbini}
      />
      <CautionTextCell note={preSale.note} />
      {!isStopSalling && !isAppliedUser && (
        <ButtonCell
          onSubmit={handleLoginModal(async () => {
            filterEmptyItem()
            await handleSubmit(v => {
              onSubmit(v)
            })()
          })}
          disabled={
            isSubmitting ||
            (watch('paymentMethod') === PaymentMethod.CreditCard &&
              !paymentSource) ||
            !isEmpty(errors) ||
            !watch('choices')?.filter(({ itemId }) => itemId).length
          }
          isSubmitting={isSubmitting}
        />
      )}
      {isAppliedUser && (
        <Stack sx={{ py: 4 }} alignItems="center">
          <Button
            variant="text"
            size="small"
            sx={{ width: 212 }}
            onClick={() => router.goBack()}
          >
            {t('back')}
          </Button>
        </Stack>
      )}
      <InformationModal
        open={membershipModalOpen}
        onClose={toggleMembershipModalOpen}
        title={t('membershipRequired', 'メンバーシップへの加入が必要です')}
        content={<></>}
        buttons={
          <Button
            size="large"
            color="primary"
            sx={{ minWidth: 212 }}
            href={generatePath(routerPages.ArtistMembership, {
              artistId: preSale?.artist?.id ?? '#',
            })}
            onClick={() => {
              /**
               * TODO:
               * 年間メンバーシッププラン機能に伴う導線実装が完了したら、先行販売に戻るUIを下記ローカルストレージキーをもとに実装すること
               * https://zpl.io/kKOkROg
               */
              setPreference('currentPreSaleId', {
                preSaleId: preSale.id,
              })
            }}
          >
            {t('seeDetail')}
          </Button>
        }
      />
    </OrganismFrame>
  )
}

const defaultValues: ApplyPreSaleLotteryValue = {
  choices: [{ itemId: '', skuId: undefined }],
  fullName: null,
  paymentMethod: PaymentMethod.CreditCard,
  preSaleId: '',
  purchaseCountChoices: [{ count: 1 }],
}
